import React, { useState } from 'react';
import WelcomeSection from '../components/WelcomeSection';
import SectionOne from '../components/SectionOne';
import Graphs from '../components/Graphs';
import GroupStationsSummary from '../components/GroupStationsSummary';
import useDaywiseTotalRevenue from '../hooks/useDaywiseTotalRevenue';
import useDaywiseTotalEnergy from '../hooks/useDaywiseTotalEnergy';
import useDaywiseTotalDuration from '../hooks/useDaywiseTotalDuration';
import useDaywiseSessions from '../hooks/useChargingSessionsCount';
import DatePicker from '../components/DatePicker';
import StationTotalsTable from '../components/StationTotalsTable';

  // Helper to generate ranges
  const generateRange = (daysBack) => {
    const today = new Date();
    const from = new Date();
    from.setDate(today.getDate() - (daysBack - 1)); // inclusive of today
    return {
      fromDate: from.toISOString().split('T')[0],
      toDate: today.toISOString().split('T')[0],
    };
  };

const Homepage = () => {
  const [selectedMetric, setSelectedMetric] = useState('Total revenue');
  const [customRange, setCustomRange] = useState(generateRange(30));

  // Hooks use customRange only
  const { revenueData, isLoading: revenueLoading, error: revenueError } =
    useDaywiseTotalRevenue(customRange);
  const { energyData, isLoading: energyLoading } =
    useDaywiseTotalEnergy(customRange);
  const { durationData, isLoading: durationLoading } =
    useDaywiseTotalDuration(customRange);
  const { sessions, isLoading: sessionsLoading } =
    useDaywiseSessions(customRange);

    // localStorage.removeItem('station_5')


  const transformApiData = (apiData, metricType) => {
    if (!apiData || !Array.isArray(apiData)) return [];

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return apiData
      .map((item) => {
        const dateString = item.session_date || item.SessionDate;
        if (!dateString) return null;

        let date;
        try {
          date = new Date(dateString);
          if (isNaN(date.getTime())) return null;
        } catch {
          return null;
        }

        const daysAgo = Math.floor((today - date) / (1000 * 60 * 60 * 24));

        let value;
        let unit;

        switch (metricType) {
          case 'Total revenue':
            value = parseFloat((item.total_revenue || 0).toFixed(1));
            unit = '₹';
            break;
          case 'Total energy (kW)':
            value = parseFloat((item.total_energy_kwh || item.TotalEnergy || 0).toFixed(1));
            unit = 'kWh';
            break;
          case 'Total sessions':
            value = parseFloat((item.session_count || item.SessionCount || 0).toFixed(1));
            unit = 'sessions';
            break;
          case 'Total hours':
            const seconds = item.total_duration_seconds || item.TotalDuration || 0;
            const minutes = seconds / 60;
            const hours = minutes / 60;
            value = parseFloat(hours.toFixed(1));
            unit = 'hrs';
            break;
          default:
            value = 0;
            unit = '';
        }

        return {
          day: Math.max(1, daysAgo + 1),
          value: value,
          label: `${value} ${unit}`,
        };
      })
      .filter((item) => item !== null);
  };

  const getApiDataForMetric = () => {
    switch (selectedMetric) {
      case 'Total revenue':
        return transformApiData(revenueData, 'Total revenue');
      case 'Total energy (kW)':
        return transformApiData(energyData, 'Total energy (kW)');
      case 'Total sessions':
        return transformApiData(sessions, 'Total sessions');
      case 'Total hours':
        return transformApiData(durationData, 'Total hours');
      default:
        return transformApiData(revenueData, 'Total revenue');
    }
  };

  const apiData = getApiDataForMetric();

  // if (revenueError) {
  //   console.error('Revenue data error:', revenueError);
  // }



  const calcDaysCount = (fromDate, toDate) => {
    const start = new Date(fromDate);
    const end = new Date(toDate);
    return Math.max(1, Math.floor((end - start) / (1000 * 60 * 60 * 24)) + 1);
  };
  
  const daysCount = customRange?.fromDate && customRange?.toDate
    ? calcDaysCount(customRange.fromDate, customRange.toDate)
    : 30; // default

  return (
    <div className="relative w-full">
      <div className="fixed inset-0 z-0 opacity-30 pointer-events-none" />
      <div className="relative z-10">
        <div className="flex flex-col gap-1 pb-2">
          <WelcomeSection />
          <div className="grid grid-cols-1 gap-2">
            <div className="lg:col-span-3">
              <div className="card p-6 w-full h-full">
                {/* Range Filter + Date Picker */}
                <div className="hidden lg:flex items-center gap-3 mb-6 relative">
                  {[
                    { label: 'Today', range: generateRange(1) },
                    { 
                      label: 'Yesterday', 
                      range: (() => {
                        const today = new Date();
                        const yesterday = new Date();
                        yesterday.setDate(today.getDate() - 1);
                        const y = yesterday.toISOString().split('T')[0];
                        return { fromDate: y, toDate: y };
                      })()
                    },
                    { label: 'Last 30 Days', range: generateRange(30) },
                    { label: 'Last 90 Days', range: generateRange(90) },
                    { label: 'Last 1 Year', range: generateRange(365) },
                  ].map((r) => (
                    <button
                      key={r.label}
                      onClick={() => setCustomRange(r.range)}
                      className={`h-8 px-3 rounded-md text-13 font-medium border transition-colors ${
                        customRange?.fromDate === r.range.fromDate && customRange?.toDate === r.range.toDate
                          ? 'bg-brand-950 text-white border-brand-950 shadow-card'
                          : 'bg-white text-neutral-700 border-neutral-200 shadow-card hover:bg-neutral-50 hover:border-neutral-300'
                      }`}
                    >
                      {r.label}
                    </button>
                  ))}

                  {/* Date Picker */}
                  <DatePicker
                    onApply={(range) => setCustomRange(range)}
                  />
                  {customRange && (
                    <span className="ml-4 text-13 text-neutral-500">
                      From: <strong className="font-semibold tabular-nums text-brand-950">{customRange.fromDate}</strong> To: <strong className="font-semibold tabular-nums text-brand-950">{customRange.toDate}</strong>
                    </span>
                  )}
                </div>

                {/* Mobile dropdown */}
                <div className="lg:hidden mb-4">
                  {(() => {
                    const options = [
                      { label: 'Today', range: generateRange(1) },
                      { label: 'Last 30 Days', range: generateRange(30) },
                      { label: 'Last 60 Days', range: generateRange(60) },
                      { label: 'Last 90 Days', range: generateRange(90) },
                      { label: 'Last 1 Year', range: generateRange(365) },
                    ];
                    const current = options.find(
                      (r) => r.range.fromDate === customRange?.fromDate && r.range.toDate === customRange?.toDate
                    );
                    return (
                      <select
                        value={current ? current.label : ''}
                        onChange={(e) => {
                          const picked = options.find((r) => r.label === e.target.value);
                          if (picked) setCustomRange(picked.range);
                        }}
                        className="input w-full"
                      >
                        {!current && <option value="" disabled>Custom range</option>}
                        {options.map((r) => (
                          <option key={r.label} value={r.label}>
                            {r.label}
                          </option>
                        ))}
                      </select>
                    );
                  })()}
                </div>

                {/* Metrics */}
                <SectionOne
                  tiles={[]}
                  onTileClick={setSelectedMetric}
                  customRange={customRange}
                />

                <StationTotalsTable customRange={customRange}/>

                {/* Graph */}
                <div className="mt-6 w-full">
                  <Graphs
                    selected={selectedMetric}
                    apiData={apiData}
                    customRange={customRange}
                    daysCount = {daysCount}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Station Summary */}
          <div className="card p-6">
            <GroupStationsSummary />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Homepage;
